import useDebounce from '@cobliteam/hooks/src/useDebounce';
import { useTranslation } from '@cobliteam/i18n';
import type React from 'react';
import { createContext, useCallback, useEffect, useState } from 'react';
import { useFleetCategories, useGlobalSidebarDef } from '../globalSidebarDef';
import { removeDiacritics } from '../helpers';
import type { Fuse } from '../types';
import { type Category, type DataWithCategory, EnumCategory, type Sidebar, type TCounts } from '../types';

interface GlobalSearchContextType {
  isOpen: boolean;
  search: string;
  category: Category;
  fleetCategories: Category[];
  sidebar: Sidebar;
  results: DataWithCategory[];
  resultsByCategory: DataWithCategory[];
  counts: TCounts;
  selectedItem: DataWithCategory | undefined;
  data: DataWithCategory[];
  fuse: Fuse<DataWithCategory>;
  setSearch: (search: string) => void;
  resetResults: () => void;
  onChangeCategory: (params: { categoryId: string; keepSearch: boolean }) => void;
  setIsOpen: (isOpen: boolean) => void;
  setSidebar: (sidebar: Sidebar) => void;
  setSelectedItem: (item: DataWithCategory | undefined) => void;
}

interface GlobalSearchProviderProps {
  children: React.ReactNode;
  data: DataWithCategory[];
  fuse: Fuse<DataWithCategory>;
  initialOpen?: boolean;
}

export const GlobalSearchContext = createContext<GlobalSearchContextType | undefined>(undefined);

const getCounts = (data: DataWithCategory[], sidebar: Sidebar) => {
  const categoriesKeys = sidebar.flatMap((group) => group.categories.map((category) => category.id));

  const counts = categoriesKeys.reduce((acc, key) => {
    acc[key as keyof TCounts] = data.filter((item) => item?.category?.id === key).length;
    return acc;
  }, {} as TCounts);
  counts.all = data.length;
  return counts;
};

const getResultsByCategory = (data: DataWithCategory[], categoryId: string) => {
  return categoryId === EnumCategory.ALL ? data : data.filter((item) => item.category.id === categoryId);
};

export function GlobalSearchProvider({ children, data, fuse, initialOpen = false }: GlobalSearchProviderProps) {
  const initialData = data;
  const { t } = useTranslation('global-search');
  const globalSidebarDef = useGlobalSidebarDef(t);
  const fleetCategories = useFleetCategories(t);
  const [sidebar, setSidebar] = useState(globalSidebarDef);
  const [isOpen, setIsOpen] = useState(initialOpen);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<Category>(sidebar[0].categories[0]);
  const [counts, setCounts] = useState<TCounts>(getCounts(initialData, sidebar));
  const [results, setResults] = useState<DataWithCategory[]>(initialData);
  const [resultsByCategory, setResultsByCategory] = useState<DataWithCategory[]>(initialData);
  const [selectedItem, setSelectedItem] = useState<DataWithCategory | undefined>(initialData[0]);
  const debouncedSearch = useDebounce(search, 400);

  const resetResults = useCallback(() => {
    const filteredResults = getResultsByCategory(initialData, category.id);
    setResults(initialData);
    setCounts(getCounts(initialData, sidebar));
    setResultsByCategory(filteredResults);
    setSelectedItem(filteredResults[0]);
  }, [category.id, initialData, sidebar]);

  const getSidebarCategoryById = (id: string) => {
    return sidebar.find((group) => group.categories.find((c) => c.id === id));
  };

  const updateResultsByCategory = (categoryId: string) => {
    const resultsFiltered = getResultsByCategory(results, categoryId);
    setResultsByCategory(resultsFiltered);
    return resultsFiltered;
  };

  const onChangeCategory = ({ categoryId, keepSearch }: { categoryId: string; keepSearch: boolean }) => {
    const groupCategory = getSidebarCategoryById(categoryId);
    const category = groupCategory?.categories.find((c) => c.id === categoryId) as Category;

    if (!keepSearch) {
      setSearch('');
      setResults(initialData);
      setCounts(getCounts(initialData, sidebar));
      const filteredResults = getResultsByCategory(initialData, categoryId);
      setResultsByCategory(filteredResults);
      setSelectedItem(filteredResults[0]);
      setCategory(category);
      return;
    }

    const resultsByCategory = updateResultsByCategory(categoryId);
    setSelectedItem(resultsByCategory[0]);
    setCategory(category);
  };

  const onSearch = useCallback(() => {
    if (debouncedSearch === '') {
      resetResults();
      return;
    }

    const normalizedSearch = removeDiacritics(debouncedSearch);
    const results = fuse.search(normalizedSearch).map((r) => r.item);

    setCounts(getCounts(results, sidebar));
    setResults(results);

    // Update resultsByCategory based on current category
    const filteredResults = category.id === EnumCategory.ALL ? results : results.filter((r) => r.category.id === category.id);
    setResultsByCategory(filteredResults);

    const firstResult = category.id === EnumCategory.ALL ? results[0] : results.find((r) => r.category.id === category.id);
    setSelectedItem(firstResult);
  }, [debouncedSearch, category.id, fuse, resetResults, sidebar]);

  // Search for results
  useEffect(() => {
    onSearch();
  }, [onSearch]);

  useEffect(() => {
    if (!search) resetResults();
  }, [search, resetResults]);

  const value: GlobalSearchContextType = {
    data,
    fuse,
    isOpen,
    search,
    category,
    sidebar,
    fleetCategories,
    results,
    resultsByCategory,
    counts,
    selectedItem,
    setSearch,
    resetResults,
    onChangeCategory,
    setIsOpen,
    setSidebar,
    setSelectedItem,
  };

  return <GlobalSearchContext.Provider value={value}>{children}</GlobalSearchContext.Provider>;
}

export default GlobalSearchProvider;
